import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  ViewEncapsulation,
  afterNextRender,
  computed,
  effect,
  input,
  output,
  viewChild,
} from '@angular/core';
import { Placeholder } from '../../../../services/voucher-design.service';
import { PlaceholderMap, escape, placeholderSample, tokenize } from '../../shared/placeholders';

const ZWSP = '​';

/**
 * A text's content with its placeholders as chips: the editable's children are
 * text nodes and non-editable chips, nothing else ever persists. The content
 * string (`Für {recipient_name}`) is what the store holds; the field serialises
 * its DOM to it on every input and rebuilds its DOM only when the content changed
 * elsewhere (undo, another element), so the caret never jumps. A small "+" follows
 * the caret and opens a picker of the values there; both live on the body so the
 * canvas zoom cannot scale them, and follow the caret through scrolls and zooms.
 */
@Component({
  selector: 'app-placeholder-field',
  standalone: true,
  template: `
    <div
      #editable
      [class]="editableClass()"
      contenteditable="true"
      spellcheck="false"
      role="textbox"
      [attr.aria-multiline]="wrap()"
      (input)="onInput($event)"
      (beforeinput)="onBeforeInput($event)"
      (keydown)="onKeydown($event)"
      (paste)="onPaste($event)"
      (drop)="$event.preventDefault()"
      (blur)="onBlur()"
      (focus)="placePlus()"
      (compositionstart)="composing = true"
      (compositionend)="onCompositionEnd()"></div>
  `,
  styles: `
    app-placeholder-field {
      display: block;
      position: relative;
    }
    app-placeholder-field .editable {
      min-height: 100%;
      min-width: 100%;
      font: inherit;
      color: inherit;
      text-align: inherit;
      line-height: inherit;
      white-space: inherit;
      overflow-wrap: anywhere;
      cursor: text;
    }
    app-placeholder-field .editable.framed {
      min-height: 4.5rem;
      white-space: pre-wrap;
    }
    app-placeholder-field .chip {
      display: inline-block;
      margin: 0 1px;
      padding: 0 4px;
      border-radius: 3px;
      background: rgba(168, 85, 247, 0.14);
      box-shadow: inset 0 -1px 0 rgba(168, 85, 247, 0.55);
      color: inherit;
      white-space: nowrap;
      user-select: none;
      cursor: default;
    }
  `,
  encapsulation: ViewEncapsulation.None, // the chips and the picker are made by hand
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaceholderFieldComponent implements OnDestroy {
  readonly content = input.required<string>();
  readonly wrap = input(false);
  readonly placeholders = input<PlaceholderMap>(new Map());
  /** Which element the content belongs to: a new one always re-renders. */
  readonly elementId = input('');
  /** Without the input chrome: the field lies over the text on the canvas and
   * takes the box's own font through the host's style. */
  readonly plain = input(false);
  /** What a chip shows: the value's name (editing shows the template) or its
   * sample, as the voucher would print it; the other one is its tooltip. */
  readonly chipText = input<'label' | 'sample'>('label');
  /** Take the focus as soon as the field appears (editing in place). */
  readonly autofocus = input(false);
  /** With `autofocus`: the whole content selected instead of the caret at the end,
   * so typing replaces a fresh text's placeholder words. */
  readonly selectAll = input(false);
  /** Enter ends the editing instead of doing nothing (a one-line text in place). */
  readonly enterCommits = input(false);
  readonly contentChange = output<string>();
  /** ⌘Z / ⌘⇧Z inside the field go to the editor's history, not the browser's. */
  readonly historyStep = output<'undo' | 'redo'>();
  /** The field lost the focus (Escape, a click elsewhere, Enter when it commits). */
  readonly blurred = output<void>();

  protected readonly editableClass = computed(() =>
    this.plain()
      ? 'editable outline-none'
      : 'editable framed border-input bg-background w-full rounded-md border px-3 py-2 text-sm shadow-xs ' +
        'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none',
  );

  private readonly editableRef = viewChild.required<ElementRef<HTMLElement>>('editable');
  private get editable(): HTMLElement {
    return this.editableRef().nativeElement;
  }
  private renderedId: string | null = null;
  private lastEmitted: string | null = null;
  /** Where the caret was when the field lost focus (the insert menu takes it). */
  private savedRange: Range | null = null;
  protected composing = false;
  /** The value picker: on the body, so a zoomed canvas cannot scale it. */
  private picker: HTMLDivElement | null = null;
  private matches: Placeholder[] = [];
  private pickerIndex = 0;
  /** How many characters before the caret the picker replaces ("{" and the query). */
  private typed = 0;
  /** The small "+" that follows the caret and opens the picker; on the body like it. */
  private plus: HTMLButtonElement | null = null;
  private readonly onSelectionChange = () => this.placePlus();
  /** The caret relative to the editable's box, so the overlays can follow a scroll
   * or a canvas zoom (the box moves and scales, the caret with it). */
  private anchor: { x: number; y: number; h: number } | null = null;
  private frame = 0;

  constructor() {
    effect(() => {
      const content = this.content();
      const id = this.elementId();
      const known = this.placeholders();
      this.wrap();
      this.chipText();
      const focused = document.activeElement === this.editable;
      // the store echoing this field's own change: leave the DOM and the caret alone
      if (id === this.renderedId && focused && this.serialise() === content) return;
      this.render(content, known);
      this.renderedId = id;
      this.lastEmitted = content;
      if (focused) this.caretToEnd();
      this.placePlus();
    });
    afterNextRender(() => {
      if (this.autofocus()) {
        this.editable.focus();
        if (this.selectAll()) this.selectAllContent();
        else this.caretToEnd();
        this.placePlus();
      }
    });
    document.addEventListener('selectionchange', this.onSelectionChange);
  }

  ngOnDestroy() {
    this.closePicker();
    document.removeEventListener('selectionchange', this.onSelectionChange);
    this.plus?.remove();
    this.plus = null;
    cancelAnimationFrame(this.frame);
  }

  /** A chip for `key` where the caret was (or at the end), as the insert menu asks. */
  insert(key: string) {
    // where the caret was before the menu took the focus; a fresh focus would land at the start
    let range: Range | null = null;
    if (this.savedRange && this.editable.contains(this.savedRange.startContainer))
      range = this.savedRange;
    const selection = window.getSelection();
    if (!range && selection?.rangeCount && this.editable.contains(selection.anchorNode))
      range = selection.getRangeAt(0).cloneRange();
    this.editable.focus();
    if (!range) {
      range = document.createRange();
      range.selectNodeContents(this.editable);
      range.collapse(false);
    }
    range.deleteContents();
    const chip = this.chip(key);
    range.insertNode(chip);
    this.normalise();
    this.caretAfter(chip);
    this.emit();
  }

  // ------------------------------------------------------------- DOM <-> content

  private chip(key: string): HTMLElement {
    const placeholder = this.placeholders().get(key);
    const label = placeholder?.label ?? key;
    const sample = placeholder ? placeholderSample(placeholder, false) : key;
    const span = document.createElement('span');
    span.className = 'chip';
    span.contentEditable = 'false';
    span.draggable = false;
    span.dataset['key'] = key;
    span.textContent = this.chipText() === 'sample' ? sample : label;
    span.title = this.chipText() === 'sample' ? label : sample;
    return span;
  }

  private isChip(node: Node | null | undefined): node is HTMLElement {
    return node instanceof HTMLElement && node.dataset['key'] !== undefined;
  }

  private render(content: string, known: PlaceholderMap) {
    this.editable.replaceChildren(
      ...tokenize(content, known).map((run) =>
        run.kind === 'text' ? document.createTextNode(run.value) : this.chip(run.key),
      ),
    );
    this.normalise();
  }

  /** What the field holds, as content: own words escaped, chips as `{key}`. */
  private serialise(): string {
    let text = '';
    for (const node of Array.from(this.editable.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE)
        text += escape((node.textContent ?? '').split(ZWSP).join(''));
      else if (this.isChip(node)) text += `{${node.dataset['key']}}`;
      else if (node.nodeName === 'BR') text += '\n';
      else text += escape((node.textContent ?? '').split(ZWSP).join(''));
    }
    return this.wrap() ? text : text.replace(/\r?\n/g, ' ');
  }

  private emit() {
    const text = this.serialise();
    if (text === this.lastEmitted) return;
    this.lastEmitted = text;
    this.contentChange.emit(text);
  }

  /**
   * A text node before a leading chip, between two chips and after a trailing one
   * (a zero-width space where there is nothing else), so the caret can land on
   * either side of every chip in every browser; then adjacent text nodes merged.
   */
  private normalise() {
    const children = Array.from(this.editable.childNodes);
    const isText = (node: Node | undefined) => node?.nodeType === Node.TEXT_NODE;
    for (const [i, node] of children.entries()) {
      if (!this.isChip(node)) continue;
      if (!isText(children[i - 1])) this.editable.insertBefore(document.createTextNode(ZWSP), node);
      if (!isText(children[i + 1])) node.after(document.createTextNode(ZWSP));
    }
    this.editable.normalize();
  }

  // ----------------------------------------------------------------- caret

  private caretAfter(chip: HTMLElement) {
    const next = chip.nextSibling;
    const range = document.createRange();
    if (next?.nodeType === Node.TEXT_NODE) {
      // past the zero-width space, so typing lands after the chip
      range.setStart(next, (next.textContent ?? '').startsWith(ZWSP) ? 1 : 0);
    } else {
      range.setStartAfter(chip);
    }
    range.collapse(true);
    this.applyRange(range);
  }

  private caretToEnd() {
    const range = document.createRange();
    range.selectNodeContents(this.editable);
    range.collapse(false);
    this.applyRange(range);
  }

  private selectAllContent() {
    const range = document.createRange();
    range.selectNodeContents(this.editable);
    this.applyRange(range);
  }

  private applyRange(range: Range) {
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    this.savedRange = range.cloneRange();
  }

  protected saveRange() {
    const selection = window.getSelection();
    if (selection?.rangeCount && this.editable.contains(selection.anchorNode))
      this.savedRange = selection.getRangeAt(0).cloneRange();
  }

  protected onBlur() {
    this.saveRange();
    this.closePicker();
    this.hidePlus();
    this.blurred.emit();
  }

  // ------------------------------------------------------------ the "+" at the caret

  /** The "+" a bit below and right of the caret while the field has the focus. */
  protected placePlus() {
    if (document.activeElement !== this.editable || this.picker) return this.hidePlus();
    const rect = this.caretRect();
    if (!rect) return this.hidePlus();
    this.anchorAt(rect);
    this.ensurePlus().style.display = '';
    this.layoutOverlays(rect);
    this.follow();
  }

  private hidePlus() {
    if (this.plus) this.plus.style.display = 'none';
  }

  /** Remember where the caret is inside the editable's box. */
  private anchorAt(rect: DOMRect) {
    const box = this.editable.getBoundingClientRect();
    this.anchor =
      box.width && box.height
        ? {
            x: (rect.right - box.left) / box.width,
            y: (rect.top - box.top) / box.height,
            h: rect.height / box.height,
          }
        : null;
  }

  /** The caret's rectangle now, from the anchor and the box as it is now. */
  private anchoredRect(): DOMRect | null {
    if (!this.anchor) return null;
    const box = this.editable.getBoundingClientRect();
    const right = box.left + this.anchor.x * box.width;
    const top = box.top + this.anchor.y * box.height;
    return new DOMRect(right, top, 0, this.anchor.h * box.height);
  }

  /** Put the "+" and the picker where the caret is. */
  private layoutOverlays(rect: DOMRect) {
    if (this.plus && this.plus.style.display !== 'none') {
      this.plus.style.left = `${Math.min(rect.right + 4, window.innerWidth - 24)}px`;
      this.plus.style.top = `${rect.bottom + 2}px`;
    }
    if (this.picker) {
      const height = Math.min(256, this.matches.length * 32 + 8);
      const below = rect.bottom + 4 + height <= window.innerHeight;
      this.picker.style.left = `${Math.min(rect.left, window.innerWidth - 300)}px`;
      this.picker.style.top = below
        ? `${rect.bottom + 4}px`
        : `${Math.max(0, rect.top - 4 - height)}px`;
    }
  }

  /** While an overlay shows, keep it at the caret through scrolls and zooms. */
  private follow() {
    if (this.frame) return;
    const tick = () => {
      this.frame = 0;
      const showing = !!this.picker || (!!this.plus && this.plus.style.display !== 'none');
      if (!showing || !this.editable.isConnected) return;
      const rect = this.anchoredRect();
      if (rect) this.layoutOverlays(rect);
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
  }

  private ensurePlus(): HTMLButtonElement {
    if (this.plus) return this.plus;
    const plus = document.createElement('button');
    plus.type = 'button';
    plus.className =
      'fixed z-50 flex h-5 w-5 items-center justify-center rounded-full text-white shadow-md';
    plus.style.background = '#a855f7';
    plus.style.font = '15px/1 system-ui, sans-serif';
    plus.textContent = '+';
    plus.title = 'Wert einfügen';
    plus.setAttribute('aria-label', 'Wert einfügen');
    // mousedown would blur the editable; the click then opens the picker at the caret
    plus.addEventListener('mousedown', (event) => event.preventDefault());
    plus.addEventListener('click', () => this.openPicker());
    document.body.appendChild(plus);
    this.plus = plus;
    return plus;
  }

  /** The picker with every value, at the caret (the "+"). */
  private openPicker() {
    this.matches = [...this.placeholders().values()];
    if (this.matches.length === 0) return;
    this.typed = 0;
    this.pickerIndex = 0;
    this.editable.focus();
    this.renderPicker();
    this.hidePlus();
  }

  /**
   * Where the caret is on screen. A collapsed range at an element boundary (the
   * end of the editable, next to a chip) has no rectangle of its own, so a marker
   * is put there for a moment and measured, and the selection put back.
   */
  private caretRect(): DOMRect | null {
    const selection = window.getSelection();
    if (!selection?.rangeCount) return null;
    const range = selection.getRangeAt(0);
    if (!this.editable.contains(range.startContainer)) return null;
    let rect = range.getBoundingClientRect();
    if (rect.width || rect.height) return rect;
    const { startContainer, startOffset } = range;
    const marker = document.createElement('span');
    marker.textContent = ZWSP;
    const probe = range.cloneRange();
    probe.collapse(true);
    probe.insertNode(marker);
    rect = marker.getBoundingClientRect();
    marker.remove();
    this.editable.normalize();
    const restored = document.createRange();
    if (startContainer.isConnected) {
      const max =
        startContainer.nodeType === Node.TEXT_NODE
          ? (startContainer.textContent ?? '').length
          : startContainer.childNodes.length;
      restored.setStart(startContainer, Math.min(startOffset, max));
    } else {
      restored.selectNodeContents(this.editable);
      restored.collapse(false);
    }
    restored.collapse(true);
    selection.removeAllRanges();
    selection.addRange(restored);
    return rect;
  }

  /** The caret as (text node, offset) inside the editable, or null. */
  private caret(): { node: Text; offset: number } | null {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !selection.isCollapsed) return null;
    const { anchorNode, anchorOffset } = selection;
    if (
      !anchorNode ||
      anchorNode.nodeType !== Node.TEXT_NODE ||
      !this.editable.contains(anchorNode)
    )
      return null;
    return { node: anchorNode as Text, offset: anchorOffset };
  }

  /** The chip the caret touches on the side a deletion would eat, or null. */
  private chipAt(direction: 'backward' | 'forward'): HTMLElement | null {
    const selection = window.getSelection();
    if (!selection?.rangeCount || !selection.isCollapsed) return null;
    const { anchorNode, anchorOffset } = selection;
    if (!anchorNode || !this.editable.contains(anchorNode)) return null;
    if (anchorNode === this.editable) {
      const node =
        this.editable.childNodes[direction === 'backward' ? anchorOffset - 1 : anchorOffset];
      return this.isChip(node) ? node : null;
    }
    if (anchorNode.nodeType !== Node.TEXT_NODE) return null;
    const text = anchorNode.textContent ?? '';
    const only = (part: string) => part.split(ZWSP).join('') === '';
    if (direction === 'backward' && only(text.slice(0, anchorOffset))) {
      const node = anchorNode.previousSibling;
      return this.isChip(node) ? node : null;
    }
    if (direction === 'forward' && only(text.slice(anchorOffset))) {
      const node = anchorNode.nextSibling;
      return this.isChip(node) ? node : null;
    }
    return null;
  }

  // ----------------------------------------------------------- value picker

  private renderPicker() {
    if (!this.picker) {
      this.picker = document.createElement('div');
      this.picker.className =
        'bg-popover text-popover-foreground fixed z-50 max-h-64 w-72 overflow-y-auto rounded-md border p-1 text-sm shadow-md';
      this.picker.setAttribute('role', 'listbox');
      document.body.appendChild(this.picker);
    }
    this.picker.replaceChildren(
      ...this.matches.map((p, i) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(i === this.pickerIndex));
        item.className =
          'flex w-full items-baseline gap-2 rounded-sm px-2 py-1.5 text-left' +
          (i === this.pickerIndex ? ' bg-accent text-accent-foreground' : '');
        const label = document.createElement('span');
        label.className = 'shrink-0';
        label.textContent = p.label;
        const sample = document.createElement('span');
        sample.className = 'text-muted-foreground min-w-0 flex-1 truncate text-xs';
        sample.textContent = p.has_value === false ? 'noch kein Wert' : p.sample;
        item.append(label, sample);
        // mousedown would blur the editable and close the picker before the click
        item.addEventListener('mousedown', (event) => event.preventDefault());
        item.addEventListener('click', () => this.pick(i));
        this.picker!.appendChild(item);
        return item;
      }),
    );
    // under the caret, in screen coordinates (unaffected by the canvas zoom), and
    // following the caret through scrolls and zooms
    const rect = this.caretRect() ?? this.editable.getBoundingClientRect();
    this.anchorAt(rect);
    this.layoutOverlays(rect);
    this.follow();
    this.picker.children[this.pickerIndex]?.scrollIntoView({ block: 'nearest' });
  }

  private closePicker() {
    this.picker?.remove();
    this.picker = null;
    this.matches = [];
    this.pickerIndex = 0;
    this.placePlus();
  }

  /** The picked value replaces the "{" and the query (or the selection) with its chip. */
  private pick(index: number) {
    const placeholder = this.matches[index];
    if (!placeholder) return this.closePicker();
    const caret = this.caret();
    const selection = window.getSelection();
    let range: Range;
    if (caret) {
      range = document.createRange();
      range.setStart(caret.node, Math.max(0, caret.offset - this.typed));
      range.setEnd(caret.node, caret.offset);
    } else if (selection?.rangeCount && this.editable.contains(selection.anchorNode)) {
      range = selection.getRangeAt(0).cloneRange();
    } else {
      range = document.createRange();
      range.selectNodeContents(this.editable);
      range.collapse(false);
    }
    range.deleteContents();
    const chip = this.chip(placeholder.key);
    range.insertNode(chip);
    this.normalise();
    this.caretAfter(chip);
    this.closePicker();
    this.emit();
  }

  // ---------------------------------------------------------------- events

  protected onInput(event: Event) {
    if (this.composing || (event as InputEvent).isComposing) return;
    this.emit();
    this.closePicker(); // typing on closes a list the "+" opened
    this.placePlus();
  }

  protected onCompositionEnd() {
    this.composing = false;
    this.emit();
  }

  protected onBeforeInput(event: InputEvent) {
    switch (event.inputType) {
      case 'insertParagraph':
      case 'insertLineBreak':
        event.preventDefault();
        if (this.picker) return; // Enter picks (see keydown)
        if (this.wrap()) document.execCommand('insertText', false, '\n');
        else if (this.enterCommits()) this.editable.blur();
        return;
      case 'historyUndo':
      case 'historyRedo':
        event.preventDefault();
        this.historyStep.emit(event.inputType === 'historyUndo' ? 'undo' : 'redo');
        return;
      case 'deleteContentBackward':
      case 'deleteContentForward': {
        // a chip goes as a whole (Firefox and Safari would eat the space next to it first)
        const chip = this.chipAt(
          event.inputType === 'deleteContentBackward' ? 'backward' : 'forward',
        );
        if (!chip) return;
        event.preventDefault();
        const previous = chip.previousSibling;
        chip.remove();
        this.normalise();
        if (previous?.nodeType === Node.TEXT_NODE) {
          const range = document.createRange();
          range.setStart(previous, (previous.textContent ?? '').length);
          range.collapse(true);
          this.applyRange(range);
        }
        this.emit();
        return;
      }
      case 'insertFromDrop':
        event.preventDefault();
        return;
    }
  }

  protected onKeydown(event: KeyboardEvent) {
    if (this.picker) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.pickerIndex = (this.pickerIndex + step + this.matches.length) % this.matches.length;
        this.renderPicker();
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        this.pick(this.pickerIndex);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.closePicker();
        return;
      }
    }
    if (event.key === 'Escape') {
      // leave the field; the editor's Escape (deselect) must not fire mid-typing
      event.stopPropagation();
      this.editable.blur();
      return;
    }
    const mod = event.metaKey || event.ctrlKey;
    const key = event.key.toLowerCase();
    if (mod && (key === 'z' || key === 'y')) {
      event.preventDefault();
      event.stopPropagation();
      this.historyStep.emit(key === 'y' || event.shiftKey ? 'redo' : 'undo');
    }
  }

  protected onPaste(event: ClipboardEvent) {
    event.preventDefault();
    let text = event.clipboardData?.getData('text/plain') ?? '';
    if (!this.wrap()) text = text.replace(/\r?\n/g, ' ');
    const selection = window.getSelection();
    if (!selection?.rangeCount || !this.editable.contains(selection.anchorNode)) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    // content copied from another text keeps its placeholders as chips
    const fragment = document.createDocumentFragment();
    for (const run of tokenize(text, this.placeholders())) {
      fragment.append(
        run.kind === 'text' ? document.createTextNode(run.value) : this.chip(run.key),
      );
    }
    const last = fragment.lastChild;
    range.insertNode(fragment);
    this.normalise();
    if (last) {
      if (this.isChip(last)) {
        this.caretAfter(last);
      } else {
        const after = document.createRange();
        after.setStart(last, (last.textContent ?? '').length);
        after.collapse(true);
        this.applyRange(after);
      }
    }
    this.emit();
  }
}
